/**
 * Что делается с готовой строкой сообщения после `formatMessage`.
 *
 * Два дела, оба — обёртка над `intl.formatMessage` в одном месте (все пути —
 * `t()`, `tr()`, `FormattedMessage`, прямой `intl.formatMessage` — идут через
 * `intl` провайдера):
 *
 *   * `uz-Cyrl`: мягкий знак у названия месяца перед приклеенным окончанием
 *     («25-ноябрьгача» → «25-ноябргача», `uzMonths.dropMonthSign`). Только для
 *     этого языка: русскому и латинице обёртка не нужна, и для них `intl`
 *     остаётся тем же объектом;
 *   * трассировка переводов (`trace.ts`) — когда включена флагом.
 *
 * Сообщение с тегами (`<b>{date}dan</b>`) приходит массивом узлов, а текст внутри
 * тега собирает функция-подстановка из кусков: исправление идёт и по строкам
 * результата, и по кускам, которые получает каждая такая функция.
 */
import type { IntlShape } from 'react-intl';
import { traceIntl } from './trace';
import { dropMonthSign } from './uzMonths';

type Rich = (chunks: unknown[]) => unknown;

const fixPart = (part: unknown): unknown => (typeof part === 'string' ? dropMonthSign(part) : part);

function fixResult(result: unknown): unknown {
  if (typeof result === 'string') return dropMonthSign(result);
  return Array.isArray(result) ? result.map(fixPart) : result;
}

function fixValues(values: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!values) return values;
  let copy: Record<string, unknown> | null = null;
  for (const [name, value] of Object.entries(values)) {
    if (typeof value !== 'function') continue;
    copy ??= { ...values };
    copy[name] = (chunks: unknown[]) => (value as Rich)(Array.isArray(chunks) ? chunks.map(fixPart) : chunks);
  }
  return copy ?? values;
}

function cyrillic(intl: IntlShape): IntlShape {
  const base = intl.formatMessage;
  const formatMessage = ((descriptor: unknown, values?: Record<string, unknown>, options?: unknown) =>
    fixResult((base as (...args: unknown[]) => unknown)(descriptor, fixValues(values), options))) as IntlShape['formatMessage'];
  return { ...intl, formatMessage };
}

/** `intl` провайдера, готовый к показу. Для русского и латиницы без трассировки — тот же объект. */
export function decorateIntl(intl: IntlShape): IntlShape {
  return traceIntl(intl.locale === 'uz-Cyrl' ? cyrillic(intl) : intl);
}
